import { useState, useEffect, useCallback } from 'react'
import { instagramService } from '@/services/instagram-service'
import type { SavedInfluencerProfile, SavedInfluencersResponse } from '@/types'

interface UseSavedInfluencersReturn {
  savedInfluencers: SavedInfluencerProfile[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  deleteSavedInfluencer: (saveId: string) => Promise<void>
}

export function useSavedInfluencers(): UseSavedInfluencersReturn {
  const [savedInfluencers, setSavedInfluencers] = useState<SavedInfluencerProfile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSavedInfluencers = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const response: SavedInfluencersResponse = await instagramService.getSavedInfluencers()
      
      if (response.success) {
        setSavedInfluencers(response.saved_profiles)
      } else {
        setError('Failed to load saved influencers')
      }
    } catch (err) {
      console.error('Error fetching saved influencers:', err)
      setError(err instanceof Error ? err.message : 'Failed to load saved influencers')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deleteSavedInfluencer = useCallback(async (saveId: string) => {
    try {
      await instagramService.deleteSavedInfluencer(saveId)
      // Remove the deleted item from the local state
      setSavedInfluencers(prev => prev.filter(influencer => influencer.save_id !== saveId))
    } catch (err) {
      console.error('Error deleting saved influencer:', err)
      throw err // Re-throw to allow the component to handle the error
    }
  }, [])

  const refetch = useCallback(async () => {
    await fetchSavedInfluencers()
  }, [fetchSavedInfluencers])

  useEffect(() => {
    fetchSavedInfluencers()
  }, [fetchSavedInfluencers])

  return {
    savedInfluencers,
    isLoading,
    error,
    refetch,
    deleteSavedInfluencer
  }
} 